import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { toNumberOrNull } from '@shared/utils';
import { useUpdateProduct, useBulkAddToCategory } from '../../hooks/useProducts';
import { useCategories } from '@modules/categories';
import { useSections } from '@modules/sections';
import { Button, Modal, Select } from '@shared/ui';

interface AddToCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId?: number;
    productIds?: number[];
    currentCategoryIds?: number[];
    onSuccess?: () => void;
}

export default function AddToCategoryModal({
    isOpen,
    onClose,
    productId,
    productIds,
    currentCategoryIds = [],
    onSuccess,
}: AddToCategoryModalProps) {
    const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

    const updateProduct = useUpdateProduct();
    const bulkAddToCategory = useBulkAddToCategory();
    const { data: sections = [] } = useSections();

    // Получаем категории для выбранной секции через API
    const { data: categoriesForSelectedSection = [] } = useCategories(selectedSectionId ?? undefined);

    // Для одиночного добавления фильтруем уже добавленные категории
    const filteredCategories = productId && currentCategoryIds.length > 0
        ? categoriesForSelectedSection.filter(category => !currentCategoryIds.includes(category.id))
        : categoriesForSelectedSection;

    const handleSectionChange = (value: number | null) => {
        setSelectedSectionId(value);
        setSelectedCategoryId(null); // Сбрасываем выбор категории при смене секции
    };

    const handleAddToCategory = async () => {
        if (!selectedCategoryId) {
            return;
        }

        try {
            const categoryId = selectedCategoryId;

            // Если передан массив productIds (массовое добавление)
            if (productIds && productIds.length > 0) {
                await bulkAddToCategory.mutateAsync({
                    productIds,
                    categoryId,
                });
            } else if (productId) {
                // Одиночное добавление
                const updatedCategoryIds = [...new Set([...currentCategoryIds, categoryId])];
                await updateProduct.mutateAsync({
                    id: productId,
                    data: {
                        categoryIds: updatedCategoryIds,
                    },
                });
            }

            // Показываем уведомление об успехе
            notifications.show({
                title: 'Успешно',
                message: productIds && productIds.length > 1
                    ? `Товары успешно добавлены в категорию`
                    : 'Товар успешно добавлен в категорию',
                color: 'green',
            });

            // Закрываем модальное окно и сбрасываем выбор
            setSelectedSectionId(null);
            setSelectedCategoryId(null);
            onClose();
            onSuccess?.();
        } catch (error) {
            console.error('Ошибка при добавлении товара в категорию:', error);
            notifications.show({
                title: 'Ошибка',
                message: 'Ошибка при добавлении товара в категорию',
                color: 'red',
            });
        }
    };

    const handleClose = () => {
        setSelectedSectionId(null);
        setSelectedCategoryId(null);
        onClose();
    };

    const sectionOptions = sections.length > 0
        ? sections.map((section) => ({
            value: section.id.toString(),
            label: section.name,
        }))
        : [{ value: '', label: 'Нет секций', disabled: true }];

    let categoryOptions;
    if (!selectedSectionId) {
        categoryOptions = [{ value: '', label: 'Сначала выберите секцию', disabled: true }];
    } else if (filteredCategories.length === 0) {
        categoryOptions = [{ value: '', label: 'Нет доступных категорий в этой секции', disabled: true }];
    } else {
        categoryOptions = filteredCategories.map((category) => ({
            value: category.id.toString(),
            label: category.name,
        }));
    }

    const isLoading = updateProduct.isPending || bulkAddToCategory.isPending;

    return (
        <Modal
            isOpen={isOpen}
            size={400}
            onClose={handleClose}
            title={productIds && productIds.length > 1 ? `Добавить ${productIds.length} товаров в категорию` : "Добавить товар в категорию"}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
                        Секция *
                    </label>
                    <Select
                        placeholder="Выберите секцию"
                        value={selectedSectionId}
                        onChange={(value) => handleSectionChange(toNumberOrNull(value))}
                        data={sectionOptions}
                        style={{ width: '100%' }}
                        clearable
                        nothingFound="Ничего не найдено"
                    />
                </div>

                <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>
                        Категория *
                    </label>
                    <Select
                        placeholder={!selectedSectionId ? "Сначала выберите секцию" : "Выберите категорию"}
                        value={selectedCategoryId}
                        onChange={(value) => setSelectedCategoryId(toNumberOrNull(value))}
                        data={categoryOptions}
                        disabled={!selectedSectionId}
                        style={{ width: '100%' }}
                        clearable
                        nothingFound="Ничего не найдено"
                    />
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button onClick={handleClose} variant='secondary'>
                        Отмена
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddToCategory}
                        disabled={!selectedCategoryId || isLoading}
                        loading={isLoading}
                    >
                        Добавить
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

